/**
 * Registrations List Page - Clara Vale V2
 *
 * Compact layout: ListPageHeader (title + subtitle + time filter)
 * + single-row toolbar with status pills, saved views, search, columns, export.
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { AppShell } from '@/admin/components/app-shell'
import { useHoldings, useHoldingStatusCounts } from '@/admin/hooks/use-products'
import { useTableState } from '@/admin/hooks/use-table-state'
import { useTableColumnPreferences } from '@/admin/hooks/use-table-column-preferences'
import { useTableFilterPreferences } from '@/admin/hooks/use-table-filter-preferences'
import { useDismissedStatusPills } from '@/admin/hooks/use-dismissed-status-pills'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { ShieldCheck } from 'lucide-react'
import {
  DataTable,
  type ActiveFilter,
} from '@/admin/components/data-table'
import {
  filterChipsSlice,
  timeRangeSlice,
  toSavedTimeRange,
  useSavedListView,
} from '@/admin/hooks/use-saved-list-view'
import { ListPageToolbar } from '@/admin/components/list-page-toolbar'
import { ListPageHeader } from '@/admin/components/shared/list-page-header'
import { BulkImportDialog } from '@/admin/components/imports/bulk-import-dialog'
import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import { useTableExport } from '@/admin/hooks/use-table-export'
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value'
import { useTranslation } from 'react-i18next'
import type { StatusPillItem } from '@/admin/components/shared/status-pills'
import type { TimeRange } from '@/admin/components/shared/time-filter'
import { timeRangeToParams } from '@/admin/lib/time-range-params'
import {
  useRegistrationTableColumns,
  HOST_ADDRESS_COLUMN_ID,
  type HoldingRow,
} from '@/admin/pages/registrations/use-registration-table-columns'

const TABLE_ID = 'registrations'

// Lock candidates: columns the table considers "structural" once they
// are on screen. A column only earns its lock after it's actually
// visible - locking a hidden column would advertise it in the column
// picker as immutable while leaving the user no way to add it.
// Only the reference (Reg #) column is structural; Product and Status
// are freely hideable.
const LOCK_CANDIDATE_IDS = ['ref']

export function RegistrationsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([])
  const {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    saveColumnVisibility,
    saveColumnOrder,
    applyDefaultColumnPreferences,
  } = useTableColumnPreferences(TABLE_ID)
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const { dismissedKeys: dismissedStatusKeys, dismiss: dismissStatusPill } =
    useDismissedStatusPills(TABLE_ID)
  const [timeRange, setTimeRange] = useState<TimeRange>('all_time')
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | undefined>()
  const bulkImportsEnabled = useFeatureFlag(FLAGS.BULK_IMPORTS)
  const [bulkRegOpen, setBulkRegOpen] = useState(false)

  const { page, pageSize, sorting, setPage, setPageSize, setSorting, queryParams } =
    useTableState(TABLE_ID)

  // Params shared by the list and the status-counts endpoints. The
  // counts endpoint intentionally ignores ``status`` so clicking a pill
  // filters the table without collapsing the pill bar to a single row.
  // The input keeps `search` so typing stays instant; the QUERY KEY reads the
  // debounced value. Keyed on the raw state, every character fired a list
  // request and a status-count request - typing "susan" sent eight, 14 in
  // flight at once, and a search that runs in ~250ms took 4-8s in production
  // purely because the requests queued behind one another.
  const debouncedSearch = useDebouncedValue(search)

  const paramsForCounts = useMemo(() => {
    const p: Record<string, string> = { ...timeRangeToParams(timeRange, customRange) }
    if (debouncedSearch) p.search = debouncedSearch
    activeFilters.forEach((f) => {
      p[f.field] = f.value
    })
    return p
  }, [debouncedSearch, activeFilters, timeRange, customRange])

  // Record-metadata filter fly-out selection, persisted per tenant+table.
  const [filterValues, setFilterValues, { applyFilterValues, restoreSavedFilterValues }] =
    useTableFilterPreferences(TABLE_ID)

  const params = useMemo(() => {
    const p: Record<string, string> = { ...queryParams, ...paramsForCounts }
    if (statusFilter) p.status = statusFilter
    // Filter fly-out selections: each facet key (source / attr__<code> /
    // product_attr__<code>) with its values comma-joined for backend OR.
    for (const [key, values] of Object.entries(filterValues)) {
      if (values.length) p[key] = values.join(',')
    }
    return p
  }, [queryParams, paramsForCounts, statusFilter, filterValues])

  const { data: holdingsData, isLoading, isFetching, isError, refetch } = useHoldings(params, { keepPreviousPage: true })

  const holdings = useMemo(() => (holdingsData?.results ?? []) as HoldingRow[], [holdingsData])
  const totalCount = holdingsData?.count ?? 0

  // Status pills come from the backend, which is the sole source of
  // truth for which buckets exist (both enum and workflow-derived) and
  // for the global counts across the full filtered dataset.
  const { data: statusCounts = [] } = useHoldingStatusCounts(paramsForCounts)
  const statusPills: StatusPillItem[] = useMemo(
    () =>
      statusCounts.map((s) => ({
        key: s.key,
        label: s.label,
        count: s.count,
      })),
    [statusCounts],
  )

  const baseColumns = useRegistrationTableColumns()

  // Gate column sortability on the BE-declared sortable_columns so only
  // genuinely orderable columns show a sort control. Falls back to the static
  // flags until the metadata arrives.
  const sortableFields = useMemo(
    () => new Set((holdingsData?.sortable_columns ?? []).map((c) => c.field)),
    [holdingsData?.sortable_columns],
  )
  const columns = useMemo(
    () =>
      sortableFields.size === 0
        ? baseColumns
        : baseColumns.map((col) =>
            col.sortField
              ? { ...col, sortable: sortableFields.has(col.sortField) }
              : col,
          ),
    [baseColumns, sortableFields],
  )

  // A saved column order predates any column added after it, and DataTable
  // sorts unknown ids to the far right - which buried the property address off
  // the edge of the table for every user who had ever touched their columns.
  // Splice it in directly behind Reg #, where it is useful, unless the user has
  // since ordered it themselves (then their choice stands).
  const effectiveColumnOrder = useMemo(() => {
    if (columnOrder.length === 0) return columnOrder
    if (columnOrder.includes(HOST_ADDRESS_COLUMN_ID)) return columnOrder
    if (!columns.some((col) => col.id === HOST_ADDRESS_COLUMN_ID)) return columnOrder
    const next = [...columnOrder]
    next.splice(columnOrder.indexOf('ref') + 1, 0, HOST_ADDRESS_COLUMN_ID)
    return next
  }, [columnOrder, columns])

  const alwaysShownColumns = useMemo(() => {
    return LOCK_CANDIDATE_IDS.filter((id) => {
      const col = columns.find((c) => c.id === id)
      if (!col) return false
      if (id in columnVisibility) return columnVisibility[id] !== false
      return col.defaultVisible !== false
    })
  }, [columns, columnVisibility])

  const { exportCsv, isExporting } = useTableExport({
    endpoint: '/registrations/',
    columns,
    columnVisibility,
    columnOrder: effectiveColumnOrder,
    followVisibleColumns: true,
    params,
  })

  // Saved views hold the whole list: columns, order, sort, the Filters menu,
  // status pill and time range.
  const savedView = useSavedListView({
    tableId: TABLE_ID,
    columns,
    columnVisibility,
    setColumnVisibility,
    saveColumnVisibility,
    applyDefaultColumnPreferences,
    columnOrder: effectiveColumnOrder,
    setColumnOrder,
    saveColumnOrder,
    lockedColumnIds: LOCK_CANDIDATE_IDS,
    slices: {
      sorting: { value: sorting ?? null, apply: setSorting, empty: null },
      filters: filterChipsSlice(activeFilters, setActiveFilters, columns),
      filterValues: {
        value: filterValues,
        apply: applyFilterValues,
        savePreference: setFilterValues,
        restorePreference: restoreSavedFilterValues,
        empty: {},
      },
      status: { value: statusFilter, apply: setStatusFilter, empty: null },
      timeRange: timeRangeSlice(timeRange, customRange, setTimeRange, setCustomRange),
    },
  })

  const handleTimeFilterChange = (range: TimeRange, custom?: { from: string; to: string }) => {
    savedView.change('timeRange', toSavedTimeRange(range, custom))
    setPage(1)
  }

  return (
    <AppShell>
      <main className="page-container py-10">
        <ListPageHeader
          title={t('registrations.title')}
          configHelp={{
            sections: ['display.reg_table'],
            returnLabel: t('registrations.title', { defaultValue: 'Registrations' }),
          }}
          totalCount={totalCount}
          highlightStat={
            statusPills.find((p) => p.key === 'active')
              ? { label: 'active', value: statusPills.find((p) => p.key === 'active')!.count }
              : undefined
          }
          timeFilterValue={timeRange}
          timeFilterCustomRange={customRange}
          onTimeFilterChange={handleTimeFilterChange}
          actionButton={{
            label: 'New Registration',
            onClick: () => navigate('/registrations/new'),
            dataTour: 'registration-actions',
          }}
          secondaryActionButton={
            bulkImportsEnabled
              ? {
                  label: 'Bulk Upload Registrations',
                  onClick: () => setBulkRegOpen(true),
                }
              : undefined
          }
        />

        {bulkImportsEnabled && (
          <BulkImportDialog
            open={bulkRegOpen}
            onOpenChange={setBulkRegOpen}
            target="registration"
            title="Bulk Upload Registrations"
            onComplete={() => void refetch()}
          />
        )}

        <ListPageToolbar<HoldingRow>
          layout="compact"
          tableId={TABLE_ID}
          search={search}
          onSearchChange={(v) => { setSearch(v); setPage(1) }}
          searchPlaceholder={t('registrations.searchPlaceholder')}
          statusPills={statusPills}
          activeStatusKey={statusFilter}
          onStatusSelect={(key) => { savedView.change('status', key); setPage(1) }}
          statusTotalCount={totalCount}
          dismissedStatusKeys={dismissedStatusKeys}
          onStatusDismiss={(key) => {
            dismissStatusPill(key)
            if (statusFilter === key) { savedView.change('status', null); setPage(1) }
          }}
          {...savedView.toolbarProps}
          columns={columns}
          activeFilters={activeFilters}
          onFiltersChange={(filters) => {
            savedView.change(
              'filters',
              filters.map(({ field, operator, value }) => ({ field, operator, value })),
            )
            setPage(1)
          }}
          filterFacets={holdingsData?.filter_options ?? []}
          filterValues={filterValues}
          onFilterValuesChange={(values) => { savedView.change('filterValues', values); setPage(1) }}
          columnVisibility={columnVisibility}
          alwaysShownColumns={alwaysShownColumns}
          columnOrder={effectiveColumnOrder}
          onExport={exportCsv}
          isExporting={isExporting}
        />

        <div data-tour="registration-list">
        <DataTable<HoldingRow>
          tableId={TABLE_ID}
          columns={columns}
          data={holdings}
          totalCount={totalCount}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          onRetry={refetch}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          sorting={sorting}
          columnOrder={effectiveColumnOrder}
          onSortingChange={(next) => savedView.change('sorting', next)}
          onRowClick={(row) => navigate(`/registrations/${row.uuid}`)}
          emptyStateNode={<EmptyState icon={ShieldCheck} title="No registrations yet" description="Product registrations will appear here." />}
          columnVisibility={columnVisibility}
          highlightQuery={debouncedSearch}
        />
        </div>
      </main>
    </AppShell>
  )
}
